import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { GaugeCircularConfiguration } from "src/model/interface/gauge-circular-configuration";
import { ShapeLayout } from "src/model/interface/shape-layout";
import { LoggerService } from "src/service/logger.service";

@Component({
    selector: "app-gauge-circular",
    templateUrl: "./gauge-circular.component.html",
    styleUrls: ["./gauge-circular.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GaugeCircularComponent implements OnInit {
  private _logContext: string = "Gauge-cucrular.component";
  private _differential: number = null;

  public gaugeStyles: any = {};
  public gaugeData: any = null;
  public gaugeOption: any = {
    series: [],
  };

  @Input("configuration") gaugeConfiguration: GaugeCircularConfiguration;
  @Input("value") gaugeValue: number;
  @Input("valueLabel") gaugeValueLabel: string;
  @Output()
  onRequestUpdateValue: EventEmitter<number> = new EventEmitter();

  constructor(
    private _loggerService: LoggerService
  ) { }

  ngOnInit() {
    this.gaugeStyles = this.getGaugeSpecificStyle();
    this.gaugeOption = this.getCircularGaugeProperties();

    this.calculateDifferential();
  }

  ngOnChanges(): void {
    this.updateGaugeValue();
  }

  /**
   * Handle click button minus on UI.
   * It's decrement current value with minor tick step
   */
  public onClickDecrementValue(): void {

    const newValue: number = this.gaugeValue - this._differential;

    if (this.gaugeValue > this.gaugeConfiguration.minValue) {
      if (newValue >= this.gaugeConfiguration.minValue) {
        this.onRequestUpdateValue.emit(newValue);
      } else {
        this.onRequestUpdateValue.emit(this.gaugeConfiguration.minValue);
      }
    }

    //this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "onClickDecrementValue()", " Current gaugeVAlue: " + this.gaugeValue + " | minorTick: " + this.gaugeConfiguration.minorTick + " | newValue: " + newValue);


  }

  /**
   * Handle click button minus on UI.
   * It's decrement current value with major tick step
   */
  public onClickIncrementValue(): void {

    const newValue: number = this.gaugeValue + this._differential;

    if (this.gaugeValue < this.gaugeConfiguration.maxValue) {
      if (newValue <= this.gaugeConfiguration.maxValue) {
        this.onRequestUpdateValue.emit(newValue);
      } else {
        this.onRequestUpdateValue.emit(this.gaugeConfiguration.maxValue);
      }
    }

    //this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "onClickIncrementValue()", " Current gaugeVAlue: " + this.gaugeValue + " | minorTick: " + this.gaugeConfiguration.minorTick + " | newValue: " + newValue);


  }

  /**
   * This method computes the numeric differential which shall be applied to the associated address' current value when one of the command buttons is pressed.
   * The calculation follows the formula:
   * 
   * ((gaugeConfiguration.maxValue - gaugeConfiguration.minValue) / gaugeConfiguration.majorTick) / gaugeConfiguration.minorTick
   */
  private calculateDifferential(): void {
    this._differential = ((this.gaugeConfiguration.maxValue - this.gaugeConfiguration.minValue) / this.gaugeConfiguration.majorTick) / this.gaugeConfiguration.minorTick;

    this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "calculateDifferential()", "Differential: " + this._differential);
  }

  /**
   * Set ngStyle property for gauge div
   *
   * @returns
   */
  private getGaugeSpecificStyle(): object {
    return {
      width: this.gaugeConfiguration.width,
      height: this.gaugeConfiguration.height,
    };
  }

  /**
   * Handle native gauge initialization properties.
   * For full component documentation, see
   * options get from https://echarts.apache.org/en/option.html#series-gauge
   */
  private getCircularGaugeProperties(): any {
    const colorSection: Array<any> = this.getColorSectionGaugeByShapeLayout();

    return {
      series: [
        {
          type: "gauge",
          detail: {
            show: false,
          },
          // startAngle: Number(this.ComponentAPI.properties["AngleStart"]), // By default is 225
          // endAngle: Number(this.ComponentAPI.properties["AngleEnd"]), // By default is -45
          data: [{ value: Number(this.gaugeValue) }],
          min: Number(this.gaugeConfiguration.minValue),
          max: Number(this.gaugeConfiguration.maxValue),
          splitNumber: Number(this.gaugeConfiguration.majorTick),
          axisTick: {
            distance: this.gaugeConfiguration.lineSize * -1,
            length: this.gaugeConfiguration.lineSize * 0.5,
            splitNumber: Number(this.gaugeConfiguration.minorTick),
            lineStyle: {
              color: "#fff",
              width: 2,
            },
          },
          pointer: { show: this.gaugeConfiguration.showPointer, length: "50%" },
          axisLabel: {
            show: this.gaugeConfiguration.showLabel,
            fontSize: 17,
            color: "auto",
            distance: -5,
          },
          axisLine: {
            lineStyle: {
              color: colorSection,
              width: this.gaugeConfiguration.lineSize,
            },
          },
        },
      ],
    };
  }

  /**
   * Get color section by shape layout
   *
   * @returns Array of section
   */
  private getColorSectionGaugeByShapeLayout(): Array<any> {
    let colorSection = [];

    if (this.gaugeConfiguration.colorRange.length > 0) {
      colorSection = this.gaugeConfiguration.colorRange
        .map((shape) => [
          this.calculateCircularGaugeSectionPercentage(
            this.gaugeConfiguration.maxValue,
            this.gaugeConfiguration.minValue,
            shape
          ),
          shape.backColor,
        ])
        .sort((a, b) => this.sortElementAscendenting(a, b)); //sort must be necessary because if shape layout are not in order, echart not rendering shape correctly
    } else {
      colorSection = [[1, "#E6EBF8"]]; //By default chart must have at least one color section
    }

    return colorSection;
  }

  /**
   * Calculate coefficient for multirange shape
   *
   * @param shape effectuve shape
   * @returns
   */
  private calculateCircularGaugeSectionPercentage(
    chartMaxValue: number,
    chartMinValue: number,
    shape: any
  ): number {
    const linearCoefficient: number = chartMaxValue - chartMinValue;

    return (shape.valueTo - chartMinValue) / linearCoefficient;
  }

  /**
   * Sort element list ascendenting
   *
   * @param a
   * @param b
   * @returns
   */
  private sortElementAscendenting(a, b): number {
    if (a[0] === b[0]) {
      return 0;
    } else {
      return a[0] < b[0] ? -1 : 1;
    }
  }

  /**
   * Update gauge value
   */
  private updateGaugeValue(): void {
    if (this.gaugeOption.series.length > 0) {
      this.gaugeData = {
        series: [
          {
            data: [{ value: Number(this.gaugeValue) }],
          },
        ],
      };
    }
  }
}
